import { TestData } from "./TestData"
import { DEMO_TIME, timeOfDay } from "./QuickStartData"
import { ApiProps, Client, StreamCallback } from "../../app/App/Client"
import { createApiClient } from "../../app/App/ApiClient"
import { AppRoute, RouteParams } from "../../app/App/Routes"
import { format } from "../../app/App/Format"
import { Localization, LanguageTextReplacements } from "../../app/App/Localization"
import { IAccount, ILog, ILogbookAppInternetStatus, ILogbookAppNMEAConnectionStatus, INavigationRecordingStatus, IAppState, ILogbookAppStatusResult, ILogbookSyncStatus, ICalculated, ICalculationResult, ILogbookModel, IModelCalculations, INotable, INote, INoteAttachment, ILogbookSession, ILogbookSessionType, IShipUser, IUser } from "../../app/Types"
import { AppViewState } from "../../app/Views/AppView"
import { ShipViewState } from "../../app/Views/ShipView"
import { AppOverlayContent, AppOverlayContentOptions } from "../../app/Views/AppOverlayContentView"
import { PrintMode, PrintRoot } from "../../app/Views/PrintView"
import { ShipViewOverlayContent, ShipViewOverlayOptions } from "../../app/Views/ShipOverlayContentView"
import { Subscription } from "../../app/Types/Generated/LogbookApi/Subscriptions"

class MockClient extends Client {
  constructor(private data?: TestData) {
    super();
  }

  async call<T>(method: string, path: string, body?: ApiProps): Promise<T> {
    const routed = MockClientData.route<T>(method, path, this.data);
    if (routed !== undefined) {
      return routed;
    }
    // Default: call parent implementation
    return super.call(method, path, body);
  }

  // Replay any seeded provisional logs (`TestData.provisionalLogs`) to each new
  // `Log/Provisional` subscriber. Provisional (in-progress, uncommitted) auto-logs are
  // deliberately NOT part of the normalized data model — a view only ever learns about
  // them over this subscription (see `Views/Day/Events.ts`) — so replaying here is what
  // lets a catalog/demo fixture seed one purely from its `TestData`, exercising the real
  // subscribe -> `AppData.updateProvisional` -> `mergeProvisional` path instead of
  // needing a dedicated view `init*` prop.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the base class's erased overload signature
  subscribe(type: string, callback: StreamCallback<any>): number {
    const id = super.subscribe(type, callback);
    if (type === Subscription.provisionalLog.type) {
      for (const log of this.data?.provisionalLogs ?? []) {
        callback(log, type, "seed");
      }
    }
    return id;
  }
}

// Pure request routing for the demo MockClient, extracted so each path's mock
// payload can be unit-tested without constructing the real Client (see
// Tests/PublicTests/README.md). Returns `undefined` when the path is not mocked,
// signalling the caller to fall through to the real implementation.
export const MockClientData = {
  route<T>(method: string, path: string, data?: TestData): T | undefined {
    // Handle trip/cruise creation check and creation
    if ((method === "GET" || method === "POST") && (path === "ship/trip" || path === "ship/cruise")) {
      const type = path.split("/")[1] as "trip" | "cruise";
      if (method === "GET") {
        // Check if creation is allowed
        return { status: "allowed" } as T;
      } else {
        // Create trip or cruise - for demo, just return success
        if (type === "trip") {
          const newTrip = data?.trips?.[0];
          return { status: "allowed", trip: newTrip } as T;
        } else {
          const newCruise = data?.cruises?.[0];
          return { status: "allowed", cruise: newCruise } as T;
        }
      }
    }
    // Handle current day request for FocusView
    if (method === "GET" && path === "ship/current/day") {
      const cruises = data?.cruises || [];
      const trips = data?.trips || [];
      const days = data?.days || [];
      const logs = data?.logs || [];
      const statuses = data?.statuses || [];
      const notes = data?.notes || [];
      const attachments = data?.attachments || [];

      // Get the latest cruise
      const latestCruise = cruises.length > 0 ? cruises[cruises.length - 1] : undefined;
      // Get the latest trip
      const latestTrip = trips.length > 0 ? trips[trips.length - 1] : undefined;
      // Get the latest day
      const latestDay = days.length > 0 ? days[days.length - 1] : undefined;
      const currentDay = latestDay?.day || 0;
      const firstDay = days.length > 0 ? days[0].day : 0;

      // Create a fake currentLog for demo if we have NMEA data
      // This represents the live/pending log entry with current sensor readings
      let currentLog: ILog | undefined = undefined;
      if (latestDay && latestTrip) {
        const { time, timeUtc, timezone } = timeOfDay(DEMO_TIME);
        currentLog = {
          // id 0 marks a pending/unsaved log — the live current log always
          // carries a data payload (default "{}").
          id: 0,
          cruiseId: latestCruise?.id || 1,
          tripId: latestTrip.id,
          dayId: latestDay.id,
          accountId: 1,
          shipId: 1,
          createdAt: DEMO_TIME.toISOString(),
          updatedAt: DEMO_TIME.toISOString(),
          time: time,
          timeUtc: timeUtc,
          timezone: timezone,
          data: "{}",
          labels: ["current"],
        };
      }

      return {
        cruises: { cruises: latestCruise ? [latestCruise] : [], calculations: [], notes: [], attachments: [] },
        trips: latestTrip ? { trips: [latestTrip], calculations: [], notes: [], attachments: [] } : undefined,
        days: { days, calculations: [], notes, attachments },
        logs: { logs, calculations: [] },
        status: statuses,
        day: currentDay,
        firstDay: firstDay,
        currentLog: currentLog,
      } as T;
    }
    // Handle trip days requests that need notes and attachments
    if (method === "GET" && path.includes("/trips/") && path.includes("/days")) {
      const tripIdMatch = path.match(/\/trips\/(\d+)\/days/);
      if (tripIdMatch) {
        const tripId = parseInt(tripIdMatch[1]);
        const days = data?.days?.filter((d) => d.tripId === tripId) || [];
        const notes = data?.notes?.filter((n) => n.tripId === tripId) || [];
        const noteIds = notes.map((n) => n.id);
        const attachments = data?.attachments?.filter((a) => noteIds.includes(a.noteId || 0)) || [];
        return { days, notes, attachments } as T;
      }
    }
    // Handle logs requests that need statuses
    if (method === "GET" && path.includes("/logs")) {
      const dayIdMatch = path.match(/\/days\/(\d+)\/logs/);
      if (dayIdMatch) {
        const dayId = parseInt(dayIdMatch[1]);
        const logs = data?.logs?.filter((l) => l.dayId === dayId) || [];
        const statuses = data?.statuses?.filter((s) => s.dayId === dayId) || [];
        return { logs, statuses } as T;
      }
    }
    // Handle track requests
    if (method === "GET" && path.includes("/track")) {
      const tracks = data?.tracks || [];
      return { tracks } as T;
    }
    // Not mocked — signal fall-through to the real implementation.
    return undefined;
  },
};

export function MockAppState(data: TestData, language: string, cloud: boolean = false): IAppState {
  const t = (text: string, replace?: LanguageTextReplacements): string => {
    return Localization.t(language, text, replace);
  };
  // For blank data (no cruises/trips started), don't set time so FocusView doesn't calculate a current day
  const hasData = data.cruises && data.cruises.length > 0 && data.cruises[0].startedAt;
  return {
    account: undefined,
    apiClient: createApiClient("app"),
    appStyles: [],
    current: undefined,
    editable: true,
    session: data.session,
    sessionType: data.sessionType || ILogbookSessionType.primary,
    shipId: data.ship?.id,
    socket: new MockClient(data),
    // Provide a mock status with nmea and sync for menu buttons
    status: {
      navigation: false,
      nmea: {
        connected: true,
        data: {},
        connectionStatus: ILogbookAppNMEAConnectionStatus.connected,
        recordingStatus: INavigationRecordingStatus.stopped,
      },
      server: false,
      sync: {
        enabled: true,
        status: ILogbookSyncStatus.current,
      },
      internet: ILogbookAppInternetStatus.connected,
      time: hasData ? new Date().toISOString() : "",
      systemTimezone: 0,
      // Fixed rather than the real build number: catalog/demo output is golden-
      // snapshotted, so a value that changes every release would churn the goldens.
      version: "2026-01-01",
    },
    theme: cloud ? "cloud" : "dark",
    user: { id: 1, name: "DemoUser" } as IUser,
    language: language,
    t: t,
    getT: (prefix: string) => {
      return (text: string, replace?: LanguageTextReplacements) => {
        return t([prefix, text].join("_"), replace);
      };
    },
    format: format,
    updateData: <T extends ILogbookModel>(data: T[], _object: T): T[] => data,
    dataWithCalculations: <T extends ILogbookModel & ICalculated<C> & INotable, C extends IModelCalculations>(data: T[], calculations?: ICalculationResult<C>[], notes?: INote[], attachments?: INoteAttachment[], id?: (note: INote) => number | undefined): T[] => {
      if (calculations) {
        data = data.map((o) => {
          const calculated = calculations.find((e) => e.id == o.id);
          o.calculated = calculated?.values;
          return o;
        });
      }
      if (notes && id) {
        data = data.map((o) => {
          let dataNotes = notes.filter((n) => {
            const i = id(n);
            return i && i == o.id;
          });
          if (attachments) {
            dataNotes = dataNotes.map((n) => {
              n.attachments = attachments.filter((a) => a.noteId == n.id);
              return n;
            });
          }
          o.notes = dataNotes;
          return o;
        });
      }
      return data;
    },
    logout: (_deleteSession?: boolean): Promise<void> => (async function () { return; })(),
    setSession: (_session: ILogbookSession, _type: ILogbookSessionType, _account?: IAccount, _user?: IUser, _features?: string[]): void => {},
    reconcileSession: (_response): void => {},
    updateSession: (_account?: IAccount, _user?: IUser): void => {},
    setShipId: (_shipId?: number): Promise<void> => (async function () { return; })(),
    forceCloseShip: (_shipId: number): Promise<void> => (async function () { return; })(),
    setLanguage: (_lang: string): void => {},
    setTheme: (_t: string): void => {},
    resetStatus: (): Promise<void> => (async function () { return; })(),
    updateStatus: (): Promise<ILogbookAppStatusResult> => (async function () {
      return {
        navigation: false,
        nmea: {
          connected: true,
          data: {},
          connectionStatus: ILogbookAppNMEAConnectionStatus.connected,
          recordingStatus: INavigationRecordingStatus.stopped,
        },
        server: false,
        sync: {
          enabled: true,
          status: ILogbookSyncStatus.current,
        },
        internet: ILogbookAppInternetStatus.connected,
        time: "",
        systemTimezone: 0,
        version: "2026-01-01",
      };
    })(),
    cloudSession: (): boolean => cloud,
    primarySession: (): boolean => !cloud,
    localSession: (): boolean => false,
    hasFeature: (feature: string): boolean => {
      return feature === "nmea" || feature === "sync" || feature === "beta" || feature === "usb";
    },
    updateCurrent: (): Promise<void> => (async function () { return; })(),
    updateRoute: (_path: string, _replace?: boolean): void => {},
    getRoute(_customRoot?: string): AppRoute { return { route: [] }; },
    openServer: (_path: string): Promise<boolean> => (async function () { return true; })(),
  };
};

export function MockAppView(data: TestData): AppViewState {
  return {
    connected: true,
    loadShips: (_force?: boolean): Promise<void> => (async function () { return; })(),
    navigateApp: (_name: string, _params?: RouteParams, _replace?: boolean): boolean => { return true; },
    route: { route: [] },
    routeName: "mock",
    ships: data.ships,
    membership: data.shipUsers,
    subscriptionExpired: false,
    deselectShip: (_shipId: number): Promise<void> => (async function () { return; })(),
    selectShip: (_shipId: number): Promise<void> => (async function () { return; })(),
    resetShip: (): void => {},
    forceCloseShip: (): void => {},
    appOverlay: AppOverlayContent.none,
    appOverlayOptions: {},
    setAppOverlay: (_content: AppOverlayContent, _options?: AppOverlayContentOptions): void => {},
    resetAppOverlay: (): void => {},
    actingShipUser: undefined,
    setActingShipUser: (_user?: IShipUser): void => {},
  };
};

export function MockShipView(data: TestData, focusActive?: boolean): ShipViewState {
  return {
    navigateShip: (_name: string, _params?: RouteParams, _replace?: boolean): boolean => { return true; },
    shipCloudEditable: false,
    shipOverlay: ShipViewOverlayContent.none,
    shipOverlayOptions: {},
    resetShipOverlay: (): void => {},
    setShipOverlay: (_content: ShipViewOverlayContent, _options?: ShipViewOverlayOptions): void => {},
    printMode: PrintMode.none,
    printRoot: undefined,
    setPrinting: (_mode: PrintMode, _object?: PrintRoot): void => {},
    shipRoute: { route: [] },
    shipRouteName: "mock",
    focusActive: focusActive ?? false,
    shipUsers: data.shipUsers,
    actingShipUser: undefined,
    setActingShipUser: (_user?: IShipUser): void => {},
  };
};
